/**
 * One exercise's sets **as one cycle prescribes them** — the read the day form makes when it is
 * opened on a week rather than on the authored base.
 *
 * The builder edits the base: the values a trainer typed, which cycle 1 of the exercise's window
 * carries verbatim. The assigned-plan calendar opens the same form on a session in week 5, and week
 * 5's numbers are the base run through the exercise's progression rule. This module is the one place
 * that translation happens.
 *
 * ── It PROJECTS nothing of its own ────────────────────────────────────────────────────────────
 * Every number here comes out of `buildMatrix`, which is the engine pinned by the 50 backend vectors
 * of `progressionParity.test.ts`. A second projection written beside it would disagree with the
 * backend on the first compound rate, and a cell the trainer then pins is PERSISTED — so the
 * disagreement would be stored data rather than a rendering slip. `findOverride` covers the three
 * fields the matrix's single axis does not reach; nothing else is computed.
 *
 * ── The origin is the exercise's ACTIVATION cycle, never the phase's first ────────────────────
 * `cycleIndex` is ABSOLUTE across the plan (1..Σ cycleCount) and the projection's offset is measured
 * from `CycleWindow.startCycle`, which `cycleWindow.ts` resolves as the exercise's own activation
 * clamped into its phase. A one-phase plan whose every exercise has been there since cycle 1 hides
 * the distinction completely, which is why it is stated here rather than left to the caller.
 *
 * A cycle BEFORE the window is not a state this app can reach from an editable session — a
 * not-started session sits at or after the currently-open cycle, and an exercise activates no later
 * than that — but it is answered rather than crashed on: the offset floors at zero inside
 * `projectValue`, so such a cycle reads the authored base.
 *
 * ── A dynamic rule has no number for a later cycle, and will not be given one ─────────────────
 * `RealMax`, `PreviousExecution` and `Amrap` resolve against the client's own history at workout
 * start (R2). Everything the matrix shows for them past the base column is an honest stand-in for a
 * screen that draws a grid; a form that invites a trainer to EDIT it would be offering to pin an
 * estimate as a prescription. So those cells are marked, the surface prints "wyliczy się w trakcie"
 * over them, and they are not pinnable.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { CycleLifespan } from './cycleLifespan';
import type { ExerciseDraft, SetDraft } from './planDraft.types';
import { axisForExercise, type AxisValueField } from './progressionAxis';
import { buildMatrix, findOverride, resolvesInSession } from './progressionMatrix';

/** Every override field the matrix's own single axis does not reach on a given exercise. */
const OVERRIDE_FIELDS: readonly AxisValueField[] = [
  'prescribedWeight',
  'prescribedPercent',
  'prescribedReps',
  'prescribedDurationSeconds',
  'prescribedDistanceMeters',
  'prescribedRounds',
  'prescribedRpe',
  'prescribedRir',
];

/** One set, resolved at one cycle, plus what a surface is allowed to do with it. */
export interface CycleSetView {
  /**
   * The set as this cycle prescribes it: the authored row with the progressing field replaced by
   * the value the rule (or a pin) puts there, and any other pinned field applied.
   */
  readonly set: SetDraft;
  /**
   * This cycle's value on the progressing field resolves at workout start, so there is no honest
   * number to show and none to edit.
   */
  readonly resolvesInSession: boolean;
  /**
   * The progressing field may be pinned at this cycle. False on the exercise's own base column
   * (that column IS the authored set — editing it means editing the set), on a warm-up row, which
   * the backend refuses an override on, and wherever `resolvesInSession` holds.
   */
  readonly pinnable: boolean;
  /**
   * Which field a pin at this cycle writes: the exercise's progression axis, or — on a back-off
   * descent — its "% of the top", which is the only per-cycle prescription such a row accepts (R10).
   */
  readonly pinField: AxisValueField;
}

/** Write one numeric field on a copy of a set, without the caller widening `SetDraft`. */
function withField(set: SetDraft, field: AxisValueField, value: number | null): SetDraft {
  return { ...set, [field]: value };
}


/**
 * The exercise's sets at one ABSOLUTE cycle.
 *
 * @param lifespan The exercise's own LIFESPAN — `findExerciseLifespan`'s answer, never the phase's
 *   span. The two coincide for an exercise present since its phase began and diverge for one added
 *   mid-plan or one that sits a cycle out, where reading the phase's origin shifts every projected
 *   value and counting elapsed cycles instead of exposures advances a rule the backend did not.
 */
export function exerciseAtCycle(
  exercise: ExerciseDraft,
  lifespan: CycleLifespan,
  cycleIndex: number,
): readonly CycleSetView[] {
  const axis = axisForExercise(exercise);
  const rows = buildMatrix(exercise, lifespan, axis);
  const dynamic = resolvesInSession(exercise, axis);

  return exercise.sets.map((set, index) => {
    const row = rows[index];
    const cell = row?.cells.find((candidate) => candidate.cycleIndex === cycleIndex);
    if (row === undefined || cell === undefined) {
      // A cycle outside the exercise's window addresses no materialised row. The authored base is
      // the honest answer and nothing about it may be pinned at a coordinate that does not exist.
      return { set, resolvesInSession: false, pinnable: false, pinField: axis.setField };
    }

    const field: AxisValueField = row.isBackoffDescent ? 'prescribedPercent' : axis.setField;
    const resolvesInSession =
      dynamic && !row.isWarmup && !row.isBackoffDescent && cell.kind === 'computed' && cell.cycleOffset > 0;

    // The three fields the single-axis matrix does not answer for. A loaded override carries
    // whatever the engine materialised in every column of a flagged row (`ProgressionOverride`'s own
    // note), so a cell this cycle really does prescribe must not read as the base.
    let projected = set;
    if (!row.isWarmup) {
      const override = findOverride(exercise.progressionOverrides, row.setIndex, cycleIndex);
      if (override !== undefined) {
        for (const other of OVERRIDE_FIELDS) {
          if (other === field) continue;
          const pinned = override[other] ?? null;
          if (pinned !== null) projected = withField(projected, other, pinned);
        }
      }
    }

    return {
      set: withField(projected, field, cell.valueRaw),
      resolvesInSession,
      pinnable: !row.isWarmup && cell.cycleOffset > 0 && !resolvesInSession,
      pinField: field,
    };
  });
}
